<template>
  <Teleport to="body">
    <div class="toasts" :aria-live="'polite'" role="status">
      <TransitionGroup name="toast">
        <div
          v-for="t in toasts"
          :key="t.id"
          :class="['toast', `toast--${t.kind}`]"
          @click="dismiss(t.id)"
        >
          <span class="toast__icon" aria-hidden="true">
            <span v-if="t.kind === 'success'">✓</span>
            <span v-else-if="t.kind === 'error'">!</span>
            <span v-else>i</span>
          </span>
          <div class="toast__body">
            <p class="toast__msg">{{ t.message }}</p>
            <p v-if="t.detail" class="toast__detail">{{ t.detail }}</p>
            <button
              v-if="t.action"
              type="button"
              class="toast__action"
              @click.stop="() => { t.action!.onClick(); dismiss(t.id); }"
            >{{ t.action.label }}</button>
          </div>
          <button
            type="button"
            class="toast__close"
            :aria-label="'Dismiss'"
            @click.stop="dismiss(t.id)"
          >×</button>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { useToast } from "../composables/useToast";

const { toasts, dismiss } = useToast();
</script>

<style scoped>
.toasts {
  position: fixed;
  top: var(--ram-space-4, 16px);
  right: var(--ram-space-4, 16px);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  gap: var(--ram-space-2, 8px);
  pointer-events: none;
  max-width: min(420px, calc(100vw - var(--ram-space-8, 32px)));
}
[dir="rtl"] .toasts { right: auto; left: var(--ram-space-4, 16px); }

.toast {
  pointer-events: auto;
  display: flex;
  align-items: flex-start;
  gap: var(--ram-space-3, 12px);
  padding: var(--ram-space-3, 12px) var(--ram-space-4, 16px);
  background: var(--ram-color-surface-raised);
  border: 1px solid var(--ram-color-border);
  border-radius: var(--ram-radius-md, 8px);
  box-shadow: var(--ram-shadow-md, 0 4px 12px rgba(0, 0, 0, 0.12));
  cursor: pointer;
  min-width: 280px;
}
.toast--success {
  border-left: 4px solid var(--ram-color-success);
}
.toast--error {
  border-left: 4px solid var(--ram-color-danger);
}
.toast--info {
  border-left: 4px solid var(--ram-color-action-500);
}

.toast__icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  font-weight: 700;
  font-size: 14px;
  flex-shrink: 0;
}
.toast--success .toast__icon {
  background: color-mix(in oklab, var(--ram-color-success) 18%, transparent);
  color: var(--ram-color-success);
}
.toast--error .toast__icon {
  background: color-mix(in oklab, var(--ram-color-danger) 18%, transparent);
  color: var(--ram-color-danger);
}
.toast--info .toast__icon {
  background: color-mix(in oklab, var(--ram-color-action-500) 18%, transparent);
  color: var(--ram-color-action-600, var(--ram-color-action-500));
}

.toast__body {
  flex: 1;
  min-width: 0;
}
.toast__msg {
  margin: 0;
  font-size: var(--ram-text-sm, 14px);
  font-weight: 500;
  color: var(--ram-color-text-primary);
  line-height: 1.35;
}
.toast__detail {
  margin: 4px 0 0 0;
  font-size: var(--ram-text-xs, 12px);
  color: var(--ram-color-text-secondary);
  line-height: 1.4;
}

.toast__close {
  background: transparent;
  border: none;
  color: var(--ram-color-text-muted);
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  padding: 0;
  width: 22px;
  height: 22px;
  border-radius: 4px;
  flex-shrink: 0;
}
.toast__close:hover {
  background: var(--ram-color-surface-muted);
  color: var(--ram-color-text-primary);
}
.toast__action {
  display: inline-block;
  margin-top: 6px;
  padding: 3px 10px;
  background: transparent;
  border: 1px solid currentColor;
  border-radius: 4px;
  font: inherit;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  opacity: 0.85;
  color: inherit;
}
.toast__action:hover { opacity: 1; }

.toast-enter-active, .toast-leave-active {
  transition: transform 0.25s ease, opacity 0.2s ease;
}
.toast-enter-from {
  transform: translateX(100%);
  opacity: 0;
}
[dir="rtl"] .toast-enter-from { transform: translateX(-100%); }
.toast-leave-to {
  opacity: 0;
  transform: scale(0.98);
}
</style>
